import { useLazyQuery } from "@apollo/client";
import { useKeenSlider } from "keen-slider/react";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { loggedInUser } from "src/apollo/cache";
import { STACK_RANK_LIST } from "src/Components/Segments";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixIcon, PhoenixMultiSelect } from "src/Components/UI/Phoenix";
import { arrow_left, arrow_right, caret_down, star_filled, xIcon } from "src/images/NewDesign";
import { OptionItem } from "src/types";
import { useDebounce, useDraggable } from "src/utils/hooks";
import { metricLabelMap } from "src/utils/misc";
import { theme } from "src/utils/theme";
import styled from "styled-components";

interface IKPIWindow {
  initialStat: string;
  formattedData: any;
  handleClose: () => void;
  timeframe: { label: string; value: any[] };
}

export const KPIWindow: React.FC<IKPIWindow> = ({ initialStat, formattedData, handleClose, timeframe }) => {
  const { draggableRef, handleDragStart } = useDraggable();

  const [stat, setStat] = useState(initialStat);

  const [sortOrder, setSortOrder] = useState("asc");
  const [sortBy, setSortBy] = useState("list_value");

  const [localTimeframe, setLocalTimeframe] = useState(timeframe.value);
  const [debouncedTimeframe, setDebouncedTimeframe] = useState(localTimeframe);

  const windowLabel = useMemo(
    () => formattedData.find((item: any) => item.metric === stat)?.label || "",
    [stat, formattedData],
  );
  const metricOptions = useMemo(
    () => Object.entries(metricLabelMap).map(([key, value]) => ({ label: value, value: key })),
    [],
  );

  const handleMetricChange = useCallback((selectedOption: OptionItem) => setStat(selectedOption.value as string), []);

  const [getStackRank, { data: dataStack, loading }] = useLazyQuery(STACK_RANK_LIST, {
    fetchPolicy: "cache-and-network",
  });

  const formattedStackRank = useMemo(() => {
    if (!dataStack?.stackRankList) return [];
    return dataStack.stackRankList
      ?.slice()
      ?.sort((a: any, b: any) => {
        if (sortBy === "row_label") {
          return sortOrder === "asc" ? a[sortBy].localeCompare(b[sortBy]) : b[sortBy].localeCompare(a[sortBy]);
        }
        return sortOrder === "desc" ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy];
      })
      ?.map((item: any) => {
        return {
          id: item.id,
          rank: item.list_rank,
          name: item.row_label,
          score: Math.round(item.list_value * 10) / 10,
          all_time_score: Math.round(item.best_all_time_value * 10) / 10,
        };
      });
  }, [dataStack, sortOrder, sortBy]);

  const handleSort = (column: string) => {
    setSortBy(column);
    if (column === sortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    }
  };

  useEffect(() => {
    if (loggedInUser()?.id && stat) {
      getStackRank({
        variables: {
          computed_id: loggedInUser()?.id,
          metric: stat,
          position: "Organization",
          lowerbound_date: moment.utc(debouncedTimeframe[0]).startOf("day").format(),
          upperbound_date: moment.utc(debouncedTimeframe[1]).endOf("day").format(),
        },
      });
    }
  }, [stat, debouncedTimeframe]);

  const handleProgressTimeframe = useCallback(() => {
    const updater = updateTimeframe(timeframe.label, true);
    if (updater) setLocalTimeframe(updater);
  }, [timeframe]);
  const handleRegressTimeframe = useCallback(() => {
    const updater = updateTimeframe(timeframe.label, false);
    if (updater) setLocalTimeframe(updater);
  }, [timeframe]);

  useDebounce(
    () => {
      setDebouncedTimeframe(localTimeframe);
    },
    [localTimeframe],
    250,
  );

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    vertical: true,
    mode: "free-snap",
    renderMode: "performance",
    slides: {
      perView: "auto",
    },
  });

  return (
    <Container ref={draggableRef}>
      <Header onMouseDown={handleDragStart}>
        <AppText fontSize={14} fontWeight={600} lineHeight={16} color={theme.text.neutral.inverse}>
          {windowLabel}
        </AppText>
        <PhoenixIcon svg={xIcon} size={16} variant="white" pointer onClick={handleClose} />
      </Header>
      <Content>
        <ContentHeader>
          <PhoenixMultiSelect
            isMulti={false}
            isClearable={false}
            options={metricOptions}
            name="kpi_window_metric"
            value={metricOptions.find((option) => option.value === stat) || null}
            onChange={handleMetricChange}
            marginBottom={false}
            width={178}
            maxHeight={32}
            minHeight={32}
          />

          <FlexDiv align="center" gap={16}>
            <AppText
              fontSize={10}
              fontWeight={600}
              lineHeight={16}
              letterSpacing={1}
              uppercase
              color={theme.text.brand.primary}
            >
              {moment(localTimeframe[0]).format("M/D/YYYY")}
              {" - "}
              {moment(localTimeframe[1]).format("M/D/YYYY")}
            </AppText>

            <FlexDiv align="center" gap={8}>
              <ArrowButton onClick={handleRegressTimeframe}>
                <PhoenixIcon svg={arrow_left} size={16} pointer variant="brand" />
              </ArrowButton>
              <ArrowButton onClick={handleProgressTimeframe}>
                <PhoenixIcon svg={arrow_right} size={16} pointer variant="brand" />
              </ArrowButton>
            </FlexDiv>
          </FlexDiv>
        </ContentHeader>

        <Row>
          <Cell width={48}>
            <AppText fontSize={10} lineHeight={14} fontWeight={600}>
              Rank
            </AppText>
          </Cell>
          <Cell width={200} onClick={() => handleSort("row_label")}>
            <AppText fontSize={10} lineHeight={14} fontWeight={600}>
              Rep
            </AppText>
            {sortBy === "row_label" && (
              <PhoenixIcon
                svg={caret_down}
                size={16}
                variant="brand"
                fillIcon
                style={{ transform: `scaleY(${sortOrder === "asc" ? 1 : -1})` }}
              />
            )}
          </Cell>
          <Cell width={84} onClick={() => handleSort("list_value")}>
            <AppText fontSize={10} lineHeight={14} fontWeight={600}>
              Score
            </AppText>
            {sortBy === "list_value" && (
              <PhoenixIcon
                svg={caret_down}
                size={16}
                variant="brand"
                fillIcon
                style={{ transform: `scaleY(${sortOrder === "asc" ? 1 : -1})` }}
              />
            )}
          </Cell>
          <Cell width={84} onClick={() => handleSort("best_all_time_value")}>
            <AppText fontSize={10} lineHeight={14} fontWeight={600} noWrap>
              Best All Time
            </AppText>
            {sortBy === "best_all_time_value" && (
              <PhoenixIcon
                svg={caret_down}
                size={16}
                variant="brand"
                fillIcon
                style={{ transform: `scaleY(${sortOrder === "asc" ? 1 : -1})` }}
              />
            )}
          </Cell>
        </Row>
        {!!formattedStackRank.length && !loading && (
          <div ref={sliderRef} className="keen-slider" style={{ maxHeight: "307px" }}>
            {formattedStackRank.map((item: any) => (
              <StackRankRow data={item} key={item.id} />
            ))}
          </div>
        )}
      </Content>
    </Container>
  );
};

const updateTimeframe = (step: string, isProgress: boolean) => {
  const operation = isProgress ? "add" : "subtract";

  const config = {
    Today: { unit: "days" as const, amount: 1, boundary: "day" as const },
    Yesterday: { unit: "days" as const, amount: 1, boundary: "day" as const },
    "This Week": { unit: "days" as const, amount: 7, boundary: "week" as const },
    "Last Week": { unit: "days" as const, amount: 7, boundary: "week" as const },
    "This Month": { unit: "months" as const, amount: 1, boundary: "month" as const },
    "Last Month": { unit: "months" as const, amount: 1, boundary: "month" as const },
    "This Year": { unit: "years" as const, amount: 1, boundary: "year" as const },
  }[step];

  if (!config) return;

  return (prev: any) => [
    moment(prev[0])[operation](config.amount, config.unit).startOf(config.boundary),
    moment(prev[1])[operation](config.amount, config.unit).endOf(config.boundary),
  ];
};

const Container = styled.div`
  position: fixed;
  z-index: 203;

  top: 100px;
  left: 694px;

  width: 424px;
  height: 424px;

  border-radius: 8px;
  background-color: ${theme.fill.neutral.primary};
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.15);

  animation: ${theme.lightPopup} 0.15s ease-in-out forwards;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;

  width: 100%;
  height: 32px;

  padding: 8px 16px;
  border-radius: 8px 8px 0px 0px;

  background-color: ${theme.PRIMARY900};
`;

const Content = styled.div`
  width: 100%;
  height: 392px;

  overflow: hidden;
  padding: 0px 4px 0px 4px;

  border: 1px solid ${theme.border.neutral.primary};
  border-width: 0px 1px 1px 1px;
  border-radius: 0px 0px 8px 8px;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 48px;

  padding: 8px 4px;
`;

const StackRankRow: React.FC<{ data: any }> = ({ data }) => {
  const rank = useMemo(() => {
    switch (data.rank) {
      case 1:
        return (
          <PhoenixIcon
            svg={star_filled}
            size={16}
            color={theme.icon.warning.default}
            hoverColor={theme.icon.warning.default}
            fillIcon
          />
        );
      case 2:
        return (
          <PhoenixIcon
            svg={star_filled}
            size={16}
            color={theme.icon.neutral.default}
            hoverColor={theme.icon.neutral.default}
            fillIcon
          />
        );
      case 3:
        return (
          <PhoenixIcon
            svg={star_filled}
            size={16}
            color={theme.icon.warning.hover}
            hoverColor={theme.icon.warning.hover}
            fillIcon
          />
        );
      default:
        return (
          <AppText fontSize={10} fontWeight={400} lineHeight={14}>
            {data.rank}
          </AppText>
        );
    }
  }, [data]);

  return (
    <Row className="keen-slider__slide">
      <Cell width={48}>{rank}</Cell>
      <Cell width={200}>
        <AppText fontSize={10} fontWeight={400} lineHeight={14}>
          {data.name}
        </AppText>
      </Cell>
      <Cell width={84}>
        <AppText fontSize={10} fontWeight={400} lineHeight={14}>
          {data.score}
        </AppText>
      </Cell>
      <Cell width={84}>
        <AppText fontSize={10} fontWeight={400} lineHeight={14}>
          {data.all_time_score}
        </AppText>
      </Cell>
    </Row>
  );
};

const Row = styled.div`
  display: flex;

  width: 100%;
  height: 32px;
  :nth-child(even) {
    background-color: ${theme.PRIMARY50};
  }
`;
const Cell = styled.div<{ width: number }>`
  display: flex;
  justify-content: center;

  min-width: ${(props) => props.width}px;
  max-width: ${(props) => props.width}px;
  height: 100%;
  padding: 8px 16px;

  :nth-child(2) {
    justify-content: flex-start;
  }
`;

const ArrowButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;

  border: none;
  border-radius: 4px;

  background-color: transparent;
  cursor: pointer;

  :hover {
    background-color: ${theme.surface.brand.secondary};
  }

  transition: background-color 0.2s ease-in-out;
`;
